import { useEffect, useState } from "react";
import { Filter, Plus, Search, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import FdeSelect from "@/frontend/features/Fde/FdeSelect";
import { compileFilters, defaultOperator, filterKind, operatorsFor } from "@/frontend/features/Fde/data/dataFilters";
import type { DataSourceFilter, ForgeAnalyticsField, ForgeQueryFilter } from "@/frontend/features/Fde/data/types";

type Props = {
  fields: ForgeAnalyticsField[];
  filters: DataSourceFilter[];
  search: string;
  onSearchChange: (value: string) => void;
  onApply: (draft: DataSourceFilter[], compiled: ForgeQueryFilter[]) => void;
  copy: Record<string, string>;
};

export default function DataFilterBar({ fields, filters, search, onSearchChange, onApply, copy }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filters);
  useEffect(() => setDraft(filters), [filters]);

  const addFilter = () => {
    const field = fields[0];
    if (!field || draft.length >= 20) return;
    setDraft((current) => [...current, { id: crypto.randomUUID(), field: field.name, operator: defaultOperator(field), value: "", endValue: "" }]);
  };

  const updateFilter = (id: string, patch: Partial<DataSourceFilter>) => {
    setDraft((current) => current.map((filter) => filter.id === id ? { ...filter, ...patch } : filter));
  };

  const apply = () => {
    const complete = draft.filter((filter) => compileFilters([filter], fields).length > 0);
    onApply(complete, compileFilters(complete, fields));
    setOpen(false);
  };

  return (
    <div className="flex min-w-0 items-center gap-2">
      <label className="relative hidden min-w-48 flex-1 md:block">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <input value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder={copy.search_sample} className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-3 text-xs outline-none transition-shadow placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/15" />
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={cn("flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium", filters.length ? "border-primary/40 bg-primary/5 text-primary" : "border-border bg-background text-muted-foreground hover:text-foreground")}>
            <Filter className="size-3.5" />{copy.filters}{filters.length > 0 && <span className="rounded bg-primary/10 px-1.5 tabular-nums">{filters.length}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[min(560px,calc(100vw-24px))] p-0">
          <div className="border-b border-border px-4 py-3">
            <p className="text-sm font-semibold">{copy.advanced_filters}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{copy.advanced_filters_help}</p>
          </div>
          <div className="max-h-72 space-y-2 overflow-y-auto p-3">
            {draft.map((filter) => <FilterRow key={filter.id} filter={filter} fields={fields} copy={copy} onChange={(patch) => updateFilter(filter.id, patch)} onRemove={() => setDraft((current) => current.filter((item) => item.id !== filter.id))} />)}
            {draft.length === 0 && <p className="py-5 text-center text-xs text-muted-foreground">{copy.no_filters}</p>}
            <button type="button" onClick={addFilter} className="flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-primary hover:bg-primary/5"><Plus className="size-3.5" />{copy.add_filter}</button>
          </div>
          <div className="flex justify-end gap-2 border-t border-border bg-muted/20 px-3 py-2.5">
            <button type="button" onClick={() => setDraft([])} className="h-8 rounded-md px-3 text-xs text-muted-foreground hover:bg-muted">{copy.clear}</button>
            <button type="button" onClick={apply} className="h-8 rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90">{copy.apply_filters}</button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function FilterRow({ filter, fields, copy, onChange, onRemove }: { filter: DataSourceFilter; fields: ForgeAnalyticsField[]; copy: Record<string, string>; onChange: (patch: Partial<DataSourceFilter>) => void; onRemove: () => void }) {
  const field = fields.find((item) => item.name === filter.field);
  const kind = filterKind(field);
  const inputType = kind === "date" ? "date" : kind === "number" ? "number" : "text";
  return (
    <div className="grid grid-cols-[minmax(120px,1fr)_150px_minmax(110px,1fr)_auto] items-center gap-2 rounded-lg border border-border/80 bg-background p-2">
      <FdeSelect ariaLabel={copy.filter_field} value={filter.field} onValueChange={(value) => { const next = fields.find((item) => item.name === value); onChange({ field: value, operator: defaultOperator(next), value: "", endValue: "" }); }} options={fields.map((item) => ({ value: item.name, label: item.label || item.name }))} size="sm" className="min-w-0 text-xs" />
      <FdeSelect ariaLabel={copy.filter_operator} value={filter.operator} onValueChange={(value) => onChange({ operator: value as DataSourceFilter["operator"], value: "", endValue: "" })} options={operatorsFor(field).map((operator) => ({ value: operator, label: copy[`filter_${operator}`] }))} size="sm" className="text-xs" />
      <div className="flex min-w-0 items-center gap-1.5">
        <input aria-label={copy.filter_start_value} type={inputType} value={filter.value} onChange={(event) => onChange({ value: event.target.value })} placeholder={copy.filter_value} className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-xs" />
        {(filter.operator === "range" || filter.operator === "timeRange") && <><span className="text-[10px] text-muted-foreground">{copy.to}</span><input aria-label={copy.filter_end_value} type={inputType} value={filter.endValue} onChange={(event) => onChange({ endValue: event.target.value })} className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-xs" /></>}
      </div>
      <button type="button" onClick={onRemove} aria-label={copy.remove_filter} className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/5 hover:text-destructive"><Trash2 className="size-3.5" /></button>
    </div>
  );
}
